import React, { useState, useEffect, useContext } from 'react';
import { Upload, Delete } from 'baseui/icon';
import { Button, SHAPE } from 'baseui/button';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _forEach from 'lodash/forEach';
import _union from 'lodash/union';
import _keys from 'lodash/keys';
import _isNil from 'lodash/isNil';
import _filter from 'lodash/filter';
import _values from 'lodash/values';
import { EMPTY_STRING, BULK_REFUND_CSV_REQUIRED_HEADERS, AppContext, BULK_REFUND_CSV_LABEL } from 'commons-ui';
import apkIcon from '../../../../assets/misc/apk.png';

import useStyles from './useStyles';
import { ASSET_TYPE_NONS3, useAssetUploadProps, AssetUploadProps, ACCEPTED_FILE_TYPES } from './constants';

const useAssetUpload = ({ value, onChange, setShowLoader, assetType, multiple = false, label }: useAssetUploadProps) => {
	const [fileData, setFileData] = useState<any>();
	const { showAlert } = useContext(AppContext);
	useEffect(() => {
		if (!_isNil(fileData)) {
			if (multiple) {
				const fileNames = _map(value, (file) => file.name);
				const newFileNames = _union(fileNames, _keys(fileData));

				onChange(
					_map(
						_filter(newFileNames, (file) => !fileData?.[file]?.isRemoved),
						(fileName) => fileData[fileName]?.file
					)
				);
			} else {
				const { isRemoved, file } = _values(fileData)[0];
				onChange(isRemoved ? undefined : file);
			}
		}
	}, [fileData]);

	const handleFileUpload = async (event: React.InputHTMLAttributes<HTMLInputElement>) => {
		setShowLoader(true);

		if (multiple) {
			const files = _get(event, ['target', 'files']);
			const newFileData = { ...fileData };
			_forEach(files, (file) => {
				newFileData[file?.name] = { file, isRemoved: false };
			});
			setFileData(newFileData);
		} else {
			const file = _get(event, ['target', 'files', 0]);
			if(label === BULK_REFUND_CSV_LABEL){
				const reader = new FileReader();
				reader.onload = (e: any) => {
					const text = e?.target?.result;
					if (typeof text !== 'string') {
						showAlert('Failed to read CSV file content');
						return;
					}
					const lines = text?.split('\n');
					const headerLine: string[] = lines[0]?.trim().split(',');

					if (!headerLine.length) {
						showAlert('CSV is empty or has no headers.');
						return;
					}
		
					const uploadedHeaders: string[] = headerLine.map((h: string) => h.trim());
		
					const isMatch = BULK_REFUND_CSV_REQUIRED_HEADERS.every((requiredHeader) => 
						uploadedHeaders.includes(requiredHeader)
					);

					if (isMatch) {
						setFileData({ [file?.name]: { file, isRemoved: false } });
					} else {
						showAlert(`Invalid headers. Expected: ${BULK_REFUND_CSV_REQUIRED_HEADERS.join(', ')}`, { type: 'error' });
					}
				};
				reader.onerror = () => {
					showAlert('Error reading the CSV file', { type: 'error' });
					setShowLoader(false);
				};
				reader.readAsText(file);
			}else{
				setFileData({ [file?.name]: { file, isRemoved: false } });
			}
		}
		setShowLoader(false);
	};

	const handleRemoveFile = (fileName: string) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		setFileData({
			...fileData,
			[fileName]: { file: fileData[fileName]?.file, isRemoved: true }
		});
		event.preventDefault();
	};

	const getImageSource = (fileToShow: string) => {
		if (assetType === ASSET_TYPE_NONS3.APK && !_isNil(fileToShow)) {
			return apkIcon;
		}
		if (assetType === ASSET_TYPE_NONS3.IMAGE) {
			return fileToShow;
		}

		return fileToShow || '';
	};

	return { handleFileUpload, handleRemoveFile, getImageSource, fileData };
};

const AssetUpload: React.FC<AssetUploadProps> = ({
	value,
	assetType,
	disabled,
	onChange,
	setShowLoader,
	multiple = false,
	downloadFile = false,
	displayText = EMPTY_STRING,
	link = EMPTY_STRING,
	name,
	label
}) => {
	const { handleFileUpload, handleRemoveFile, getImageSource } = useAssetUpload({
		value,
		assetType,
		onChange,
		setShowLoader,
		multiple,
		label
	});
	const styles = useStyles();

	const iconStyleProps = { className: disabled ? styles.disabledStyle : styles.iconActiveStyle };

	const renderFile = (fileName: string) => {
		return (
			<>
				{assetType === ASSET_TYPE_NONS3.IMAGE ? (
					<img src={getImageSource(fileName)} className={styles.imageStyle} alt='' />
				) : (
					fileName
				)}
				<Button shape={SHAPE.circle} size='mini' onClick={handleRemoveFile(fileName)} {...iconStyleProps}>
					<Delete size={20} {...iconStyleProps} />
				</Button>
			</>
		);
	};
	const renderValue = () => {
		if (multiple) {
			return (
				<>
					{_map(value, (file) => (
						<>{renderFile(file?.name)}</>
					))}
				</>
			);
		}
		return renderFile(value?.name);
	};

	return (
		<>
			<div className={disabled ? [styles.containerStyle, styles.disabledStyle].join(' ') : styles.containerStyle}>
				<label htmlFor={`asset-input-${name}`}>
					<Upload size={40} />
				</label>
				<input
					id={`asset-input-${name}`}
					type='file'
					className={styles.inputStyle}
					accept={ACCEPTED_FILE_TYPES[assetType]}
					onChange={handleFileUpload}
					disabled={disabled}
					multiple={multiple}
				/>
				{(!multiple && value) || (multiple && value && value.length > 0) ? (
					<div className={styles.imageContainerStyle}>{renderValue()}</div>
				) : null}
			</div>
			{downloadFile ? (
				<a href={link} className={styles.downloadFileContainerStyle}>
					{displayText}
				</a>
			) : null}
		</>
	);
};

export default AssetUpload;
